import http from "@/utils/http"
import {round} from "lodash";

declare interface UploadConfig {
    config_url: string,
    file: File,
    onProgress?: Function,
    onSuccess?: Function,
    onError?: Function,
}

async function retry(fn, max_times = 3) {
    let time = 0;
    while (1) {
        http.show_error_message = time >= max_times - 1
        try {
            return await fn()
        } catch (error) {
            time++
            if (time >= max_times) {
                error = new Error(error.response.data.message);
                throw error
            }
        }
    }
}


export function dropFileIntoContainer(element: Element, cb: Function) {
    element.addEventListener('dragenter', e => {
        e.stopPropagation()
        e.preventDefault()
    }, false);
    element.addEventListener('dragover', e => {
        e.stopPropagation()
        e.preventDefault()
    }, false);
    element.addEventListener('drop', (e: DragEvent) => {
        if (cb instanceof Function) {
            cb(e.dataTransfer.files)
        }
        e.stopPropagation()
        e.preventDefault()
    }, false);
}

export function selectFile(cb: Function, accept: string = '') {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    if (accept) {
        input.setAttribute('accept', accept)
    }
    input.onchange = () => {
        if (cb instanceof Function) {
            cb(input.files)
        }
    }
    input.click()
}

export async function upload(config: UploadConfig) {
    try {
        const config_res = await http.get(config.config_url, {
            params: {
                type: config.file.type,
                filename: config.file.name,
                size: config.file.size,
            },
        })
        const upload_config = config_res.data
        const formData = new FormData()
        Object.keys(upload_config?.body || {}).map(k => {
            formData.append(k, upload_config?.body[k])
        })
        formData.append('file', config.file)
        const res = await http[upload_config.method](upload_config.url, formData, {
            withCredentials: false,
            onUploadProgress: p => {
                if (config.onProgress instanceof Function) {
                    return config.onProgress(100 * p.loaded / p.total)
                }
            },
        })
        if (config.onSuccess instanceof Function) {
            config.onSuccess(res)
        }
        return res
    } catch (e) {
        if (config.onError instanceof Function) {
            config.onError(e)
        }
        throw e
    }

}

export async function multipartUpload(config: UploadConfig) {
    const res = await http.get(config.config_url, {
        params: {
            type: config.file.type,
            filename: config.file.name,
            size: config.file.size,
        },
    })
    let i = 0
    while (res.data.parts[i]) {
        const part = res.data.parts[i]
        let policyRes
        await retry(async () => {
            policyRes = await http.get(res.data.chunk_policy_url, {
                params: {
                    key: res.data.key,
                    uploadId: res.data.uploadId,
                    partNumber: i + 1,
                }
            })
            const uploadRes = await http.put(
                policyRes.data.url,
                config.file.slice(part.seekTo, part.seekTo + part.length),
                {
                    withCredentials: false,
                    headers: {
                        'Content-Type': '',
                        'Authorization': '',
                    }
                }
            )
        })

        config.onProgress(i / res.data.parts.length * 100)
        i++
    }

    let completeRes

    await retry(async () => {
        completeRes = await http.post(res.data.complete_url)
        config.file['id'] = completeRes.data.id
    })
    config.onSuccess(completeRes);
}

export function formatFile(file) {
    const getNameByUrl = url => {
        if (!url) {
            return ''
        }
        return url.match(/^.*\/([.\w]*)(\?.*)?$/)[1] || ''
    }
    const calcExt = file => {
        if (!file) {
            return ''
        }
        if (!file.name) {
            file.name = getNameByUrl(file.url)
        }
        const res = file.name.match(/^.+\.(\w+)$/)
        return res ? res[1] : ''
    }

    const calcType = file => {
        const ext = calcExt(file)
        if (['jpeg', 'jpg', 'webp', 'png', 'bmp', 'gif'].indexOf(ext) !== -1) {
            return 'image'
        }
        if (['xls', 'xlsx', 'doc', 'docx', 'txt', 'pdf'].indexOf(ext) !== -1) {
            return 'document'
        }
        if (['mp4', 'mpg', 'rm', 'rmvb', 'avi', 'mpeg'].indexOf(ext) !== -1) {
            return 'video'
        }
        if (['zip', 'rar', '7z', 'tar', 'gz'].indexOf(ext) !== -1) {
            return 'zip'
        }
        return 'unknown'
    }

    file['ext'] = calcExt(file)
    if (!file['name'] && file['url']) {
        file['name'] = getNameByUrl(file['url'])
    }
    if (!file['size']) {
        file['size'] = 0
    }

    file['sizeText'] = calcSizeText(file['size'])
    file['typeClass'] = calcType(file)
    return file
}

export function calcSizeText(size) {
    if (size < 1024) {
        return size + 'B'
    }
    if (size < 1024 * 1024) {
        return round(size / 1024, 2) + 'K'
    }
    if (size < 1024 * 1024 * 1024) {
        return round(size / 1024 / 1024, 2) + 'M'
    }
    return round(size / 1024 / 1024 / 1024, 2) + 'G'

}

export function readAsBase64(file) {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);
        fileReader.onload = () => {
            resolve(fileReader.result as string);
        }
    })
}
